import React, { useEffect, useRef, useState } from 'react'

import { observer } from 'mobx-react'
import { FieldErrors, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'

import cn from 'classnames'
import { useDebouncedCallback } from 'use-debounce'
import * as yup from 'yup'

import { yupResolver } from '@hookform/resolvers/yup'
import { ClickAwayListener, Tooltip } from '@mui/material'
import { makeStyles, styled } from '@mui/material/styles'

import { ViewTypeEditor } from 'utils'

import { toast } from 'App'
import { ActivatedUser } from 'components/ActivatedUser'
import { EditorTextView } from 'components/EditorTextView'

import { ISearch } from 'models/search'

import { useStore } from 'store'

import { ReactComponent as CloseIcon } from 'sources/images/close.svg'
import emptyPhoto from 'sources/images/emptyPhoto.png'
import { ReactComponent as LoaderInner } from 'sources/images/loader/loaderInner.svg'
import { ReactComponent as LoaderOuter } from 'sources/images/loader/loaderOuter.svg'
import { ReactComponent as SearchIcon } from 'sources/images/search.svg'

import { UserOnlineMark } from '../../UserOnlineMark'

import styles from './styles.module.scss'

const PREFIX = 'Search'

const classes = {
  tooltip: `${PREFIX}-tooltip`,
  popper: `${PREFIX}-popper`,
}

const Root = styled('form')(({ theme }) => ({
  [`& .${classes.tooltip}`]: {
    maxWidth: '310px',
    width: '98vw',
    margin: 0,
    backgroundColor: 'transparent',
    '@media (max-width: 555px)': {
      paddingRight: '5px',
      maxWidth: '100vw',
      width: '100vw',
    },
  },

  [`& .${classes.popper}`]: {
    maxWidth: 'calc(min(310px, 98vw))',
    width: '98vw',
    backgroundColor: 'transparent',
    '@media (max-width: 555px)': {
      maxWidth: '100vw',
      width: '100vw',
    },
  },
}))

interface IProps {
  transparent?: boolean
  linkColor?: string
  positionSticky?: boolean
}

const SearchSchema = yup.object().shape({
  search: yup
    .string()
    .test('zeroLength', value => !(value?.length === 0))
    .max(24, 'Search request should be less than 24 characters')
    .test(
      'emojiAndSmiles',
      'You are not allowed to use emojis or smileys',
      value => !/\p{Extended_Pictographic}/u.test(value as string)
    ),
  // .test(
  //   'symbols',
  //   'You are not allowed to use symbols',
  //   (value) => !(/[!§±$%^&*()_+=\[\]{};:"\\|,.<>\/?~]|[^\x00-\x7F]/.test(value as string)),
  // ),
})

//Resize
const Search: React.FC<IProps> = observer(
  ({ linkColor, transparent, positionSticky }) => {
    const { main, profile } = useStore()
    const navigate = useNavigate()
    const searchRef: any = useRef(null)

    const [open, setOpen] = useState<boolean>(false) //-◄-Status opened search input
    const [loading, setLoading] = useState<boolean>(false) //-◄-Status loading info
    const [onFocus, setOnFocus] = useState<boolean>(false) //-◄-Status focuses on input
    const [searchList, setSearchList] = useState<ISearch | null>(null) //-◄-Find info list
    const { register, handleSubmit, getValues, setValue } = useForm({
      resolver: yupResolver(SearchSchema),
      mode: 'onChange',
      reValidateMode: 'onChange',
    })

    const {
      onChange: onChangeRegister,
      onBlur,
      name: nameRegister,
      ref: refRegister,
    } = register('search')
    const useFormData = getValues()
    const useFormValueSearch = useFormData.search ? useFormData.search : ''

    const searchInputRef = useRef<HTMLInputElement | null>(null)

    const handlerCloseSearchTooltip = () => {
      setOnFocus(false)
      setOpen(false)
      clearSearchData()
      searchInputRef.current?.blur()
    }

    const clearSearchData = () => {
      setValue('search', '')
      setSearchList(null)
    }

    useEffect(() => {
      window.addEventListener('scroll', handlerCloseSearchTooltip)

      return () =>
        window.removeEventListener('scroll', handlerCloseSearchTooltip)
    }, [])

    // useOutsideClick(searchRef, handlerCloseSearchTooltip)

    const handlerOpenSearchTooltip = () => {
      clearSearchData()
      // setOnFocus(false)
      if (open) {
        setOnFocus(false)
      } else {
        setTimeout(() => {
          searchInputRef.current?.focus()
        }, 100)
      }
      setOpen(prev => !prev)
    }

    const debouncedOnChangeSearchField = useDebouncedCallback(
      (value: Record<string, string>) => {
        onChangeSearchField(value.search)
      },
      500
    )

    const onChangeSearchField = async (value: string) => {
      if (value.length >= 1) {
        try {
          setLoading(true)
          let response = await main.search(value)
          setSearchList(response)
          setLoading(false)
        } catch (e: any) {
          setLoading(false)
          console.error(e.massage)
        }
      }
    }

    const onChangeSearchError = (err: FieldErrors) => {
      if (err.search?.type === 'zeroLength') {
        clearSearchData()
      } else {
        toast({
          type: 'warning',
          message: String(err.search?.message),
        })
      }
    }

    const selectUser = async (value: string) => {
      handlerCloseSearchTooltip()
      navigate(`/user/${value}`)
    }

    const selectTag = async (id: number) => {
      handlerCloseSearchTooltip()
      navigate(`/hashtags/${id}`)
    }

    const selectTagPublication = async (id: number) => {
      handlerCloseSearchTooltip()
      navigate(`/post/${id}`)
    }

    const getSearchListLength = () => {
      if (searchList) {
        return Object.keys(searchList).reduce((prev, next) => {
          const item = searchList[next as keyof ISearch]
          return item ? item.length + prev : prev
        }, 0)
      }
    }

    const getViewSearchList = () => {
      const getListItem = (
        item: any,
        onClick: (value: any) => void,
        type: 'user' | 'tag' | 'publication'
      ) => {
        return (
          <div
            onClick={() => onClick(type === 'user' ? item.NickName : item.Id)}
            className={cn({
              [styles.searchItem]: true,
              [styles.border]: true,
            })}
          >
            <div className={styles.imageBlock}>
              {type === 'tag' ? (
                <SearchIcon className={styles.searchTag} />
              ) : (
                <>
                  <img
                    src={item.ImageLink ? item.ImageLink : emptyPhoto}
                    className={cn({
                      [styles.emptyImage]: !item.ImageLink,
                    })}
                    alt=""
                  />

                  <UserOnlineMark nickName={item.NickName} />
                </>
              )}
            </div>
            <div
              className={cn({
                [styles.identifierBlock]: true,
                [styles.identifierBlockTag]: type === 'tag',
              })}
            >
              {type === 'tag' ? (
                <div className={styles.name}>{item.PublicationTag}</div>
              ) : (
                <>
                  <div className={styles.name}>{`${
                    item.FirstName ? item.FirstName : ''
                  } ${item.LastName ? item.LastName : ''}`}</div>
                  <div className={styles.nickNameBlock}>
                    <div className={styles.nickName}>
                      @{item.NickName ? item.NickName : ''}
                    </div>
                    <ActivatedUser
                      activated={item.Activated}
                      style={{ width: '15px', minWidth: '15px' }}
                    />
                  </div>
                </>
              )}
              {type === 'publication' ? (
                <div className={styles.text}>
                  <EditorTextView
                    text={item.Text}
                    viewType={ViewTypeEditor.text}
                  />
                </div>
              ) : null}
            </div>
          </div>
        )
      }

      if (Object.keys(getValues()).length === 0) return null

      if (loading) {
        return (
          <div className={styles.loaderBox}>
            <LoaderOuter className={styles.loaderOuter} />
            <LoaderInner className={styles.loaderInner} />
          </div>
        )
      }

      // if (getValues().search.length === 0) {
      //   return (
      //     <div className={styles.textLabel}>Try searching for people or tag</div>
      //   )
      // }

      if (useFormValueSearch.length >= 1 && getSearchListLength() === 0) {
        return <div className={styles.textLabel}>Information not found</div>
      }

      if (useFormValueSearch.length >= 1) {
        return (
          <div className={styles.searchListItems}>
            {searchList?.Users?.map((item, idx) => {
              return (
                <React.Fragment key={idx}>
                  {getListItem(item, selectUser, 'user')}
                </React.Fragment>
              )
            })}
            {searchList?.Tags?.map((item, idx) => {
              return (
                <React.Fragment key={idx}>
                  {getListItem(item, selectTag, 'tag')}
                </React.Fragment>
              )
            })}
            {searchList?.Publications?.map((item, idx) => {
              return (
                <React.Fragment key={idx}>
                  {getListItem(item, selectTagPublication, 'publication')}
                </React.Fragment>
              )
            })}
          </div>
        )
      }
    }

    return (
      <Root
        onSubmit={event => event.preventDefault()}
        onChange={handleSubmit(
          debouncedOnChangeSearchField as any,
          onChangeSearchError
        )}
        className={cn({
          [styles.wrapper]: true,
        })}
        autoComplete="off"
        id="searchField"
      >
        <Tooltip
          classes={{
            popper: styles.popper,
            tooltip: classes.tooltip,
            popperArrow: styles.popperArrow,
          }}
          // onClose={handlerCloseSearchTooltip}
          open={open}
          disableFocusListener
          disableHoverListener
          disableTouchListener
          placement="left"
          // arrow
          title={
            <ClickAwayListener onClickAway={handlerCloseSearchTooltip}>
              <div
                // onClick={e => e.preventDefault()}
                ref={searchRef}
                className={cn({
                  [styles.searchBlock]: true,
                })}
              >
                <input
                  placeholder={'Try searching for people or tag'}
                  autoComplete="off"
                  onFocus={() => setOnFocus(true)}
                  className={cn({
                    [styles.input]: true,
                  })}
                  type="text"
                  onChange={onChangeRegister}
                  onBlur={onBlur}
                  ref={e => {
                    refRegister(e)
                    searchInputRef.current = e
                  }}
                  name={nameRegister}
                />
                <CloseIcon
                  style={{
                    display: useFormValueSearch.length > 0 ? 'block' : 'none',
                  }}
                  className={cn({
                    [styles.closeIcon]: true,
                  })}
                  onClick={clearSearchData}
                />
                {onFocus && (
                  <div className={styles.searchList}>{getViewSearchList()}</div>
                )}
              </div>
            </ClickAwayListener>
          }
        >
          <button
            type="button"
            onClick={handlerOpenSearchTooltip}
            className={styles.searchBtn}
          >
            <SearchIcon
              className={cn({
                [styles.searchIcon]: true,
              })}
            />
          </button>
        </Tooltip>
      </Root>
    )
  }
)

Search.displayName = 'Search'

export default Search
